// src/components/exam-builder/ExcelImportModal.jsx
import React, { useMemo, useState } from "react";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  previewExcelImport,
  commitExcelImport,
} from "../../services/api/examExcel";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function ExcelImportModal({ isOpen, onClose, token }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);

  const navigate = useNavigate();

  const canCommit = useMemo(() => {
    return (
      !!file && !!preview && preview.ok === true && !preview.errors?.length
    );
  }, [file, preview]);

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoadingPreview(false);
    setCommitting(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const runPreview = async (f) => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const data = await previewExcelImport(token, f);
      setPreview(data);

      if (data?.ok) {
        toast.success(
          `Preview موفق ✅ (${data?.summary?.itemsCount || 0} ردیف)`,
        );
      } else {
        toast.warn("Preview انجام شد ولی خطا دارد");
      }
    } catch (e) {
      toast.error(e.message || "Preview ناموفق بود");
      setPreview({
        ok: false,
        errors: [{ row: 0, message: e.message || "Preview failed" }],
        warnings: [],
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePick = async (f) => {
    setFile(f);
    await runPreview(f);
  };

  const handleCommit = async () => {
    if (!canCommit) return;
    setCommitting(true);
    try {
      const res = await commitExcelImport(token, file);
      toast.success("ایمپورت انجام شد ✅");

      const newExamId = res?.newExamId;
      if (newExamId) {
        // منتقل شو به builder آزمون جدید (همون draft جدید)
        navigate(`/organization/exam-builder?draft=${newExamId}`);
        // اگر شما مسیر/پارامتر دیگری دارید، همینجا تغییر بده
      } else {
        toast.info("آزمون جدید ساخته شد ولی شناسه برنگشت.");
      }

      handleClose();
    } catch (e) {
      const msg = e.message || "Commit ناموفق بود";
      toast.error(msg);
      // اگر بک‌اند errors برگردوند:
      if (e.details?.errors)
        setPreview((p) => ({
          ...(p || {}),
          ok: false,
          errors: e.details.errors,
        }));
    } finally {
      setCommitting(false);
    }
  };

  const errors = preview?.errors || [];
  const warnings = preview?.warnings || [];

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl overflow-hidden">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            <div className="font-bold text-gray-800">
              آپلود و ایمپورت اکسل سوالات
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* body */}
        <div className="p-5 space-y-4">
          {/* file picker */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700">
              <UploadCloud className="w-4 h-4" />
              انتخاب فایل اکسل
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handlePick(f);
                  e.target.value = "";
                }}
              />
            </label>

            <div className="text-sm text-gray-700">
              {file ? (
                <span className="font-medium">{file.name}</span>
              ) : (
                <span className="text-gray-500">فایلی انتخاب نشده</span>
              )}
            </div>

            {file && (
              <button
                onClick={() => runPreview(file)}
                disabled={loadingPreview}
                className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              >
                {loadingPreview ? "در حال بررسی..." : "اجرای Preview دوباره"}
              </button>
            )}
          </div>

          {/* summary */}
          {preview && (
            <div className="rounded-xl border p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                {preview.ok ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <div className="font-bold text-emerald-700">
                      Preview موفق
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <div className="font-bold text-amber-700">
                      Preview دارای خطا
                    </div>
                  </>
                )}
              </div>

              <div className="text-sm text-gray-700 flex flex-wrap gap-6">
                <div>
                  تعداد سکشن‌ها: <b>{preview?.summary?.sectionsCount ?? "-"}</b>
                </div>
                <div>
                  تعداد ردیف‌ها: <b>{preview?.summary?.itemsCount ?? "-"}</b>
                </div>
                <div>
                  تعداد خطاها: <b>{errors.length}</b>
                </div>
                <div>
                  تعداد هشدارها: <b>{warnings.length}</b>
                </div>
              </div>
            </div>
          )}

          {/* errors */}
          {errors.length > 0 && (
            <div className="rounded-xl border p-4">
              <div className="font-bold text-red-700 mb-2">خطاها</div>
              <div className="max-h-56 overflow-auto space-y-2">
                {errors.slice(0, 200).map((e, idx) => (
                  <div key={idx} className="text-sm text-gray-800">
                    <span className="font-bold text-red-700">
                      ردیف {e.row}:
                    </span>{" "}
                    {e.message}
                  </div>
                ))}
                {errors.length > 200 && (
                  <div className="text-xs text-gray-500">
                    … فقط 200 خطای اول نمایش داده شد
                  </div>
                )}
              </div>
            </div>
          )}

          {/* warnings */}
          {warnings.length > 0 && (
            <div className="rounded-xl border p-4">
              <div className="font-bold text-amber-700 mb-2">هشدارها</div>
              <div className="max-h-40 overflow-auto space-y-2">
                {warnings.slice(0, 200).map((w, idx) => (
                  <div key={idx} className="text-sm text-gray-800">
                    <span className="font-bold text-amber-700">هشدار:</span>{" "}
                    {w.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
          >
            بستن
          </button>

          <button
            onClick={handleCommit}
            disabled={!canCommit || committing}
            className={`px-4 py-2 rounded-lg text-white ${
              canCommit
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {committing ? "در حال ثبت..." : "ثبت نهایی (Commit)"}
          </button>
        </div>
      </div>
    </div>
  );
}
